import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Project, projectService } from '../services/projectService';
import { useAuth } from './AuthContext';

interface ProjectContextType {
  currentProject: Project | null;
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  hasNoProjects: boolean;
  switchProject: (projectId: string) => Promise<void>;
  createProject: (name: string, description?: string, color?: string) => Promise<Project>;
  updateProject: (projectId: string, data: Partial<Project>) => Promise<void>;
  archiveProject: (projectId: string) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  refreshProjects: () => Promise<void>;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const CURRENT_PROJECT_KEY = 'currentProjectId';

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    if (!isAuthenticated) {
      setProjects([]);
      setCurrentProject(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await projectService.getProjects({ limit: 100 });

      if (response.success) {
        const projectList = response.data || [];
        setProjects(projectList);

        // Restore last selected project from localStorage
        const savedProjectId = localStorage.getItem(CURRENT_PROJECT_KEY);

        if (savedProjectId) {
          const savedProject = projectList.find((p: Project) => p.id === savedProjectId);
          if (savedProject && savedProject.is_active) {
            setCurrentProject(savedProject);
          } else if (projectList.length > 0) {
            // Fallback to first active project
            const firstActive = projectList.find((p: Project) => p.is_active);
            setCurrentProject(firstActive || null);
            if (firstActive) {
              localStorage.setItem(CURRENT_PROJECT_KEY, firstActive.id);
            }
          } else {
            // No projects at all - clear stale project ID from localStorage
            localStorage.removeItem(CURRENT_PROJECT_KEY);
          }
        } else if (projectList.length > 0) {
          // No saved project, use first active
          const firstActive = projectList.find((p: Project) => p.is_active);
          setCurrentProject(firstActive || null);
          if (firstActive) {
            localStorage.setItem(CURRENT_PROJECT_KEY, firstActive.id);
          }
        }
      }
    } catch (err: any) {
      console.error('Error loading projects:', err);
      setError(err.message || 'Erro ao carregar projetos');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const switchProject = useCallback(async (projectId: string) => {
    try {
      const response = await projectService.switchProject(projectId);

      if (response.success) {
        const project = projects.find(p => p.id === projectId);
        if (project) {
          setCurrentProject(project);
          localStorage.setItem(CURRENT_PROJECT_KEY, projectId);
          window.location.reload();
        }
      }
    } catch (err: any) {
      console.error('Error switching project:', err);
      throw err;
    }
  }, [projects]);

  const createProject = useCallback(async (name: string, description?: string, color?: string) => {
    try {
      const response = await projectService.createProject({
        name,
        description,
        color
      });

      if (response.success) {
        const newProject = response.data;
        setProjects(prev => [newProject, ...prev]);
        setCurrentProject(newProject);
        localStorage.setItem(CURRENT_PROJECT_KEY, newProject.id);
        return newProject;
      }

      throw new Error(response.message || 'Erro ao criar projeto');
    } catch (err: any) {
      console.error('Error creating project:', err);
      throw err;
    }
  }, []);

  const updateProject = useCallback(async (projectId: string, data: Partial<Project>) => {
    try {
      const response = await projectService.updateProject(projectId, data);

      if (response.success) {
        setProjects(prev =>
          prev.map(p => (p.id === projectId ? { ...p, ...response.data } : p))
        );

        if (currentProject?.id === projectId) {
          setCurrentProject(prev => (prev ? { ...prev, ...response.data } : null));
        }
      }
    } catch (err: any) {
      console.error('Error updating project:', err);
      throw err;
    }
  }, [currentProject]);

  const archiveProject = useCallback(async (projectId: string) => {
    try {
      const response = await projectService.archiveProject(projectId);

      if (response.success) {
        setProjects(prev =>
          prev.map(p => (p.id === projectId ? { ...p, is_active: false } : p))
        );

        // If archived project was current, switch to another
        if (currentProject?.id === projectId) {
          const nextActive = projects.find(p => p.id !== projectId && p.is_active);
          if (nextActive) {
            setCurrentProject(nextActive);
            localStorage.setItem(CURRENT_PROJECT_KEY, nextActive.id);
          } else {
            setCurrentProject(null);
            localStorage.removeItem(CURRENT_PROJECT_KEY);
          }
        }
      }
    } catch (err: any) {
      console.error('Error archiving project:', err);
      throw err;
    }
  }, [currentProject, projects]);

  const deleteProject = useCallback(async (projectId: string) => {
    try {
      const response = await projectService.deleteProject(projectId);

      if (response.success) {
        setProjects(prev => prev.filter(p => p.id !== projectId));

        // If deleted project was current, switch to another
        if (currentProject?.id === projectId) {
          const nextActive = projects.find(p => p.id !== projectId && p.is_active);
          if (nextActive) {
            setCurrentProject(nextActive);
            localStorage.setItem(CURRENT_PROJECT_KEY, nextActive.id);
          } else {
            setCurrentProject(null);
            localStorage.removeItem(CURRENT_PROJECT_KEY);
          }
        }
      }
    } catch (err: any) {
      console.error('Error deleting project:', err);
      throw err;
    }
  }, [currentProject, projects]);

  const refreshProjects = useCallback(async () => {
    await loadProjects();
  }, [loadProjects]);

  // Check if user has no projects (after loading is complete)
  const hasNoProjects = !isLoading && isAuthenticated && projects.length === 0;

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        projects,
        isLoading,
        error,
        hasNoProjects,
        switchProject,
        createProject,
        updateProject,
        archiveProject,
        deleteProject,
        refreshProjects
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;
